import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { CheckCircleFilled, HistoryOutlined } from "@ant-design/icons";
import { SbTag, SbButton, SbTooltip } from "uiCore";
import {
  SuggestedRolesWrapperStyled,
  SuggestedRoleRowStyled,
} from "../RedeploymentReductionDetailV2.styles";
import ShortlistTag from "../../components/ShortlistTag";
import NextRoleMatchTag from "../../components/NextRoleMatchTag";
import { useNextRolesForPerson } from "../../useTalentMobilityData";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { useTargetedRoleIdsForEmployee } from "Store/useRedeploymentPlanStore";

const { Text } = Typography;

// One occupant row's "What can they do" panel — their next 2-3 best-matched
// roles, plus the "Mark as Available" action for this reduction. Mock people
// carry their roles precomputed; API people fetch POST
// /talent-mobility/next-roles when the panel mounts (see
// useNextRolesForPerson). Both arrive sorted best-match first, excluding the
// person's current role.
const AtRiskDetailPanel = ({ person, currentRoleJdId, currentRoleTitle, currentRoleTarget }) => {
  const thresholds = useMatchThresholds();
  const navigate = useNavigate();
  const targetedRoleIds = useTargetedRoleIdsForEmployee(currentRoleJdId, person.id);
  const { nextRoles, isLoading } = useNextRolesForPerson(person);
  const suggestedRoles = useMemo(() => nextRoles.slice(0, 3), [nextRoles]);

  return (
    <SuggestedRolesWrapperStyled>
      <div className="nested-title">Next best-matched roles for {person.name}</div>
      {isLoading ? (
        <Text type="secondary">Loading next roles…</Text>
      ) : suggestedRoles.length ? (
        suggestedRoles.map((m) => (
          <SuggestedRoleRowStyled key={m.roleId}>
            <span>
              {m.role?.isPast && (
                <SbTooltip content="This employee previously held this role — the assignment has ended.">
                  <SbTag color="gold" icon={<HistoryOutlined />} style={{ marginRight: 6, fontWeight: 600 }}>
                    Past Role
                  </SbTag>
                </SbTooltip>
              )}
              {m.role?.title}
              <ShortlistTag roleId={m.roleId} personId={person.id} />
              {/* Same per-role tag as Employee View's next-roles table —
                  this role is on the person's targeted_jd_sb_unique_ids
                  (marked available from its Candidate Profile). */}
              {targetedRoleIds.has(m.roleId) && (
                <SbTag color="success" icon={<CheckCircleFilled />} style={{ marginLeft: 6 }}>
                  Recommended
                </SbTag>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <NextRoleMatchTag sri={m.sri} thresholds={thresholds} />
              <SbButton
                buttonType="secondary"
                size="small"
                onClick={() =>
                  // current_role tells the Candidate Profile's Shortlist which
                  // redeployment plan to append this (person, role) pair to;
                  // current_role_title + target let the profile page replicate
                  // this reduction's header (and its working Export CSV/Save
                  // Plan buttons) instead of its default "Back" header.
                  navigate(
                    `/talent-mobility/candidate/${person.id}/role/${m.roleId}?index=smi&current_role=${currentRoleJdId}&current_role_title=${encodeURIComponent(currentRoleTitle)}&target=${currentRoleTarget}`
                  )
                }
              >
                View Profile
              </SbButton>
            </span>
          </SuggestedRoleRowStyled>
        ))
      ) : (
        <Text type="secondary">No matched next roles for this person yet.</Text>
      )}
    </SuggestedRolesWrapperStyled>
  );
};

export default AtRiskDetailPanel;
